import React, { useContext } from 'react';

import { MDText } from 'i18n-react';

import { Button, PaperIcon } from '@motorway/mw-highway-code';

import { submitDocuments } from 'PrivateApis';

import { FSL_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsFinanceSettlementLetter';
import { toPrice } from 'Utilities/formatter';
import { useRetrieveConfirmationSale } from 'Utilities/hooks/useRetrieveConfirmationSale';

import { DocumentCaptureConfirmationProgressContext, DocumentCaptureConfirmationProgressContextType, FinanceSettlementDetails } from '../../DocumentCaptureContexts';
import DocumentCaptureButtons from '../../shared/DocumentCaptureButtons/DocumentCaptureButtons';

import FinanceProviderCompleteSectionTexts from './FinanceProviderCompleteSection.texts.json';

import styles from './FinanceProviderCompleteSection.scss';

interface FinanceProviderCompleteSectionProps {
  enquiryId: string;
  provider: string;
  settlementDetails: FinanceSettlementDetails;
}

const PageTexts = new MDText(FinanceProviderCompleteSectionTexts);

export const FinanceProviderCompleteSection = (props: FinanceProviderCompleteSectionProps) => {
  const { enquiryId, provider, settlementDetails } = props;

  const {
    setProgress,
  } = useContext<DocumentCaptureConfirmationProgressContextType>(DocumentCaptureConfirmationProgressContext);

  const handleRetrieveConfirmationSaleSuccess = (data: DocumentCaptureConfirmationProgressContextType['progress']) => {
    if (setProgress) {
      setProgress(data);
    }
  };
  const { retrieveConfirmationSale } = useRetrieveConfirmationSale({
    onSuccess: handleRetrieveConfirmationSaleSuccess,
  });
  const handleSubmitAndContinue = async () => {
    FSL_SNOW_PLOW_EVENTS.CONTINUE_SUCCESS_CLICK();

    await submitDocuments({ enquiryId: parseInt(enquiryId), kind: 'finance_settlement_letter' });
    await retrieveConfirmationSale();
  };

  return (
    <section className={styles.financeCompleteSection} data-testid='financeCompleteSection'>
      <h2 className={styles.title}>{PageTexts.translate('title')}</h2>

      <div className={styles.summaryTextContainer}>
        <p className={styles.summaryText}>{PageTexts.translate('summaryText')}</p>
      </div>
      <br/>
      <table
        aria-hidden="true"
        className={styles.table}
        data-testid='resultsTable'
        width={'100%'}
      >
        <thead></thead>
        <tbody>
          <tr key={1}>
            <td>{PageTexts.translate('table.financeTitle')}</td>
            <td>
              <p className={styles.figure} data-testid={'figure'}>{toPrice(settlementDetails?.figure)}</p>
              <p className={styles.expiry} data-testid={'expiry'}>{PageTexts.translate('table.expiry')} {settlementDetails?.expiry}</p>
            </td>
          </tr>
          <tr key={2}>
            <td>{PageTexts.translate('table.providerTitle')}</td>
            <td className={styles.provider} data-testid={'provider'}>{provider?.toUpperCase()}</td>
          </tr>
          <tr key={3}>
            <td>{PageTexts.translate('table.docTitle')}</td>
            <td>
              <p className={styles.letterLink}>
                <span className={styles.iconTextWrapper}>
                  <PaperIcon/>
                  <a download data-testid={'downloadPdf'} href={settlementDetails?.documentLocation} onClick={() => {
                    FSL_SNOW_PLOW_EVENTS.DOWNLOAD_LETTER_CLICK();
                  }} target="_blank">
                    <span className={styles.desktopText}>{PageTexts.translate('table.docText')}</span>
                    <span className={styles.mobileText}>{PageTexts.translate('table.docTextMobile')}</span>
                  </a>
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <DocumentCaptureButtons>
        <Button
          fullWidth
          data-testid='submitAndContinue'
          icon="chevron"
          label='Continue'
          onClick={handleSubmitAndContinue}
        />
      </DocumentCaptureButtons>
    </section>);
};
